import { AxiosResponse } from "axios";
import { useCallback, useEffect, useState } from "react";

type UseAPIHookLazy<R> = {
  data: R | null;
  loading: boolean;
  error: any;
  refetch: (cb?: () => Promise<AxiosResponse<R>>) => void;
};

type UseAPIHook<R> = [
  () => Promise<void>,
  {
    data: R | null;
    loading: boolean;
    error: any;
    refetch: (cb?: () => Promise<AxiosResponse<R>>) => void;
  }
];

export type APIRequestCallback<R> = () => Promise<AxiosResponse<R>>;

type APICallbackObjectWrapper<R> = {
  cb: APIRequestCallback<R>;
};

/**
 * Wraps a request function in a hook, allowing us to maintain a common
 * code structure across component level state
 * @param cb
 */
const useApi = <R = any>(
  cb: () => Promise<AxiosResponse<R>>
): UseAPIHook<R> => {
  // This may change, so we actually keep a reference to it here
  const [callbackObjectWrapper, setCllbackObjectWrapper] = useState<
    APICallbackObjectWrapper<R>
  >({ cb });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState();
  const [data, setData] = useState<R | null>(null);
  /**
   * When a new callback is set, we need to ensure it refetches next render
   * When this is true, @executeRequest is called
   */
  const [shouldRefetch, setShouldRefetch] = useState<boolean>(false);

  /**
   * Needs wrapped in a callback since we are wrapping _cb with this function, and it
   * could change. We don't want to recreate this function everytime something else changes
   * as that will set an infinite loop off
   */
  const executeRequest = useCallback(async () => {
    setLoading(true);
    try {
      const r = await callbackObjectWrapper.cb();
      setData(r.data);
    } catch (error) {
      setError(error as any);
    }
    setLoading(false);
  }, [callbackObjectWrapper]);

  useEffect(() => {
    if (!shouldRefetch) return;
    setShouldRefetch(false);
    executeRequest();
  }, [callbackObjectWrapper, shouldRefetch]);

  const refetch = (cb?: () => Promise<AxiosResponse<R>>) => {
    setData(null);
    if (cb) {
      setShouldRefetch(true);
      setCllbackObjectWrapper({ cb });
    } else executeRequest();
  };

  return [executeRequest, { data, loading, error, refetch }];
};

/**
 * Wraps a request function in hook, allowing us to maintain a common
 * code structure across component level state.
 *
 * Works the same way as @useApi except it calls the function immediately after the component is mounted
 * @param cb
 */
const useApiLazy = <R = any>(
  cb: () => Promise<AxiosResponse<R>>
): UseAPIHookLazy<R> => {
  const [executeRequest, { data, error, loading, refetch }] = useApi(cb);

  useEffect(() => {
    executeRequest();
  }, []);

  return { data, loading, error, refetch };
};

export { useApi, useApiLazy };
